import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, keyframes } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { SmartInput } from '../components/SmartInput';
import type { SmartInputMode } from '../components/SmartInput';
import { auth } from '../services/auth';
import { api } from '../services/api';
import { useConfigStore } from '../stores/useConfigStore';

const logoSvg = process.env.PUBLIC_URL + '/logo.svg';

// ============================================================================
// Constants
// ============================================================================

const NEON = '#DCF995';
const SURFACE = 'rgba(255,255,255,0.06)';
const BORDER = 'rgba(255,255,255,0.1)';
const MUTED = 'rgba(255,255,255,0.4)';
const TEXT_WHITE = '#ffffff';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ============================================================================
// Animations
// ============================================================================

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeOutUp = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(-24px); }
`;

const errorPopIn = keyframes`
  0%   { opacity: 0; transform: translateY(8px) scale(0.95); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * Steps:
 *  email → password → (newPassword | verify | resetCode → resetPassword)
 * Only the first two are on the "happy path"; the others are entered when
 * Cognito reports a challenge or the user clicks "Forgot password?".
 */
type LoginStepType = 'email' | 'password' | 'newPassword' | 'verify' | 'resetCode' | 'resetPassword';

interface StepConfig {
  type: LoginStepType;
  question: string;
  subtitle: string;
}

const STEP_CONFIG: Record<LoginStepType, StepConfig> = {
  email: { type: 'email', question: 'Welcome back', subtitle: 'Enter your email to sign in' },
  password: { type: 'password', question: 'Enter your password', subtitle: '' },
  newPassword: { type: 'newPassword', question: 'Create a new password', subtitle: 'Your account requires a password update' },
  verify: { type: 'verify', question: 'Verify your email', subtitle: 'Enter the 6-digit code we just sent you' },
  resetCode: { type: 'resetCode', question: 'Check your email', subtitle: 'Enter the 6-digit password reset code' },
  resetPassword: { type: 'resetPassword', question: 'Choose a new password', subtitle: 'At least 8 characters with uppercase, lowercase, and numbers' },
};

const HAPPY_PATH: LoginStepType[] = ['email', 'password'];

function friendlyAuthError(err: any, fallback: string): string {
  const name = err?.name || '';
  const msg = err?.message || '';
  if (name === 'NotAuthorizedException' || msg.includes('Incorrect username or password')) return 'Incorrect email or password';
  if (name === 'UserNotFoundException' || msg.includes('User does not exist')) return 'Incorrect email or password';
  if (name === 'UserNotConfirmedException' || msg.includes('User is not confirmed')) return 'Please verify your email first';
  if (name === 'InvalidPasswordException' || msg.includes('Password does not conform')) return 'Password must include uppercase, lowercase, and numbers';
  if (name === 'CodeMismatchException') return 'That code is not correct';
  if (name === 'ExpiredCodeException') return 'That code has expired. Request a new one.';
  if (name === 'LimitExceededException' || name === 'TooManyRequestsException') return 'Too many attempts. Please wait a few minutes and try again.';
  if (name === 'PasswordResetRequiredException') return 'A password reset is required. Use "Forgot password?" below.';
  return msg || fallback;
}

// ============================================================================
// Main Component
// ============================================================================

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');

  const [stepType, setStepType] = useState<LoginStepType>('email');
  const [animating, setAnimating] = useState(false);
  const [animDir, setAnimDir] = useState<'in' | 'out'>('in');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(
    searchParams.get('verified') ? 'Email verified. Sign in to continue.' : null,
  );
  const [loading, setLoading] = useState(false);

  const currentStep = STEP_CONFIG[stepType];
  const stepIndex = HAPPY_PATH.indexOf(stepType);
  const normalizedEmail = email.trim().toLowerCase();

  // ---- Navigation ----

  const goToStep = useCallback((next: LoginStepType) => {
    setError(null);
    setAnimDir('out');
    setAnimating(true);
    setTimeout(() => {
      setStepType(next);
      setAnimDir('in');
      setTimeout(() => setAnimating(false), 300);
    }, 200);
  }, []);

  const goBack = useCallback(() => {
    if (stepType !== 'email') goToStep(stepType === 'resetPassword' ? 'resetCode' : 'email');
  }, [stepType, goToStep]);

  /** After Amplify reports a completed sign-in: warm the token cache, load config, go in. */
  const finishSignIn = useCallback(async () => {
    const token = await api.getAuthToken(true);
    if (!token) throw new Error('Signed in but no session token was returned. Please try again.');
    await useConfigStore.getState().loadConfig(true);
    navigate('/overview', { replace: true });
  }, [navigate]);

  const attemptLogin = useCallback(async () => {
    const result = await auth.login(normalizedEmail, password);
    switch (result.nextStep) {
      case 'DONE':
        await finishSignIn();
        return;
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        goToStep('newPassword');
        return;
      case 'CONFIRM_SIGN_UP':
        try { await auth.resendSignUpCode(normalizedEmail); } catch { /* code may already be in flight */ }
        goToStep('verify');
        return;
      case 'RESET_PASSWORD':
        await auth.requestPasswordReset(normalizedEmail);
        goToStep('resetCode');
        return;
      case 'MFA_REQUIRED':
        throw new Error('Multi-factor authentication is enabled on this account but not yet supported in this dashboard.');
      default:
        throw new Error(`Unexpected sign-in step: ${result.rawStep || 'unknown'}`);
    }
  }, [normalizedEmail, password, finishSignIn, goToStep]);

  // ---- Validation & Advance ----

  const validateAndAdvance = useCallback(async () => {
    setError(null);
    setInfo(null);

    switch (stepType) {
      case 'email':
        if (!EMAIL_RE.test(normalizedEmail)) {
          setError('Please enter a valid email');
          return;
        }
        goToStep('password');
        return;

      case 'password': {
        if (!password) {
          setError('Please enter your password');
          return;
        }
        setLoading(true);
        try {
          await attemptLogin();
        } catch (err: any) {
          const name = err?.name || '';
          if (name === 'UserNotConfirmedException' || String(err?.message).includes('User is not confirmed')) {
            try { await auth.resendSignUpCode(normalizedEmail); } catch { /* ignore */ }
            goToStep('verify');
          } else if (name === 'PasswordResetRequiredException') {
            try {
              await auth.requestPasswordReset(normalizedEmail);
              goToStep('resetCode');
            } catch (e: any) {
              setError(friendlyAuthError(e, 'Could not start password reset'));
            }
          } else {
            setError(friendlyAuthError(err, 'Login failed'));
          }
        } finally {
          setLoading(false);
        }
        return;
      }

      case 'newPassword': {
        if (newPassword.length < 8) {
          setError('Password must be at least 8 characters');
          return;
        }
        setLoading(true);
        try {
          await auth.confirmSignIn(newPassword);
          await finishSignIn();
        } catch (err: any) {
          setError(friendlyAuthError(err, 'Failed to set new password'));
        } finally {
          setLoading(false);
        }
        return;
      }

      case 'verify': {
        if (code.length !== 6) {
          setError('Please enter the 6-digit code');
          return;
        }
        setLoading(true);
        try {
          await auth.confirmSignUp(normalizedEmail, code);
          setCode('');
          if (password) {
            await attemptLogin();
          } else {
            setInfo('Email verified. Enter your password to sign in.');
            goToStep('password');
          }
        } catch (err: any) {
          setError(friendlyAuthError(err, 'Invalid verification code'));
        } finally {
          setLoading(false);
        }
        return;
      }

      case 'resetCode': {
        if (code.length !== 6) {
          setError('Please enter the 6-digit code');
          return;
        }
        goToStep('resetPassword');
        return;
      }

      case 'resetPassword': {
        if (newPassword.length < 8) {
          setError('Password must be at least 8 characters');
          return;
        }
        setLoading(true);
        try {
          await auth.confirmPasswordReset(normalizedEmail, code, newPassword);
          setPassword(newPassword);
          setCode('');
          const result = await auth.login(normalizedEmail, newPassword);
          if (result.nextStep === 'DONE') {
            await finishSignIn();
          } else {
            setInfo('Password updated. Please sign in.');
            goToStep('password');
          }
        } catch (err: any) {
          setError(friendlyAuthError(err, 'Failed to reset password'));
          if (err?.name === 'CodeMismatchException' || err?.name === 'ExpiredCodeException') goToStep('resetCode');
        } finally {
          setLoading(false);
        }
        return;
      }
    }
  }, [stepType, normalizedEmail, password, newPassword, code, goToStep, attemptLogin, finishSignIn]);

  const handleForgotPassword = useCallback(async () => {
    setError(null);
    if (!EMAIL_RE.test(normalizedEmail)) {
      setError('Enter your email first');
      return;
    }
    setLoading(true);
    try {
      await auth.requestPasswordReset(normalizedEmail);
      setInfo(`We sent a reset code to ${normalizedEmail}`);
      goToStep('resetCode');
    } catch (err: any) {
      setError(friendlyAuthError(err, 'Could not start password reset'));
    } finally {
      setLoading(false);
    }
  }, [normalizedEmail, goToStep]);

  const handleResendCode = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      if (stepType === 'verify') await auth.resendSignUpCode(normalizedEmail);
      else await auth.requestPasswordReset(normalizedEmail);
      setInfo('A new code is on its way');
    } catch (err: any) {
      setError(friendlyAuthError(err, 'Could not resend the code'));
    } finally {
      setLoading(false);
    }
  }, [stepType, normalizedEmail]);

  // ---- SmartInput props ----

  const smartInputProps = useMemo(() => {
    switch (stepType) {
      case 'email':
        return { mode: 'text' as SmartInputMode, value: email, onChange: setEmail, placeholder: 'you@company.com', inputType: 'email' };
      case 'password':
        return { mode: 'password' as SmartInputMode, value: password, onChange: setPassword, placeholder: 'Your password' };
      case 'newPassword':
      case 'resetPassword':
        return { mode: 'password' as SmartInputMode, value: newPassword, onChange: setNewPassword, placeholder: 'Choose a new password' };
      case 'verify':
      case 'resetCode':
        return { mode: 'code' as SmartInputMode, value: code, onChange: setCode };
      default:
        return { mode: 'text' as SmartInputMode, value: '', onChange: () => {} };
    }
  }, [stepType, email, password, newPassword, code]);

  const inputHint = (() => {
    switch (stepType) {
      case 'password': return 'Enter to sign in';
      case 'newPassword':
      case 'resetPassword': return '8+ characters';
      case 'verify':
      case 'resetCode': return '6-digit code';
      default: return 'Enter to continue';
    }
  })();

  const subtitle = stepType !== 'email' && normalizedEmail && !currentStep.subtitle
    ? normalizedEmail
    : currentStep.subtitle;

  const showResend = stepType === 'verify' || stepType === 'resetCode';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0a1a 50%, #1B0826 100%)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 4 }, pt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {stepType !== 'email' && (
            <Box
              onClick={goBack}
              sx={{
                cursor: 'pointer', color: MUTED, display: 'flex', alignItems: 'center', transition: 'color 0.2s',
                '&:hover': { color: TEXT_WHITE },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 22 }} />
            </Box>
          )}
          <img src={logoSvg} alt="LogoPulse" style={{ height: 32, width: 'auto' }} />
        </Box>

        {stepIndex >= 0 && (
          <Typography sx={{ fontSize: '13px', color: NEON, fontWeight: 600, letterSpacing: '0.5px' }}>
            {stepIndex + 1} / {HAPPY_PATH.length}
          </Typography>
        )}
      </Box>

      {/* Question area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: { xs: 2, sm: 4 } }}>
        <Box
          key={stepType}
          sx={{
            textAlign: 'center',
            width: '100%',
            maxWidth: '780px',
            animation: animating
              ? (animDir === 'out' ? `${fadeOutUp} 0.2s ease-out forwards` : `${fadeInUp} 0.3s ease-out forwards`)
              : `${fadeInUp} 0.3s ease-out forwards`,
          }}
        >
          <Typography sx={{ fontSize: { xs: '36px', sm: '52px', md: '60px' }, fontWeight: 700, color: TEXT_WHITE, mb: 1.5, lineHeight: 1.1 }}>
            {currentStep.question}
          </Typography>
          <Typography sx={{ fontSize: { xs: '16px', sm: '18px' }, color: MUTED }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Bottom input area */}
      <Box sx={{ px: { xs: 2, sm: 4 }, pb: { xs: 3, sm: 5 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Error / info chip */}
        <Box sx={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          {error && (
            <Box
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderRadius: '12px',
                bgcolor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                animation: `${errorPopIn} 0.25s ease-out`,
              }}
            >
              <Typography sx={{ fontSize: '13px', color: 'rgba(239,68,68,0.9)', fontWeight: 500 }}>{error}</Typography>
              <Box
                onClick={() => setError(null)}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'rgba(239,68,68,0.5)', transition: 'color 0.15s', '&:hover': { color: 'rgba(239,68,68,0.9)' } }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          )}
          {!error && info && (
            <Box
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderRadius: '12px',
                bgcolor: 'rgba(220,249,149,0.1)', border: '1px solid rgba(220,249,149,0.2)',
                animation: `${errorPopIn} 0.25s ease-out`,
              }}
            >
              <Typography sx={{ fontSize: '13px', color: NEON, fontWeight: 500 }}>{info}</Typography>
            </Box>
          )}
        </Box>

        {/* Input + button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', maxWidth: '780px' }}>
          <Box
            sx={{
              height: '100px',
              flex: 1,
              borderRadius: '20px',
              bgcolor: SURFACE,
              border: '1px solid',
              borderColor: BORDER,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              position: 'relative',
              transition: 'border-color 0.3s, box-shadow 0.4s',
              '&:focus-within': {
                borderColor: 'rgba(220,249,149,0.25)',
                boxShadow: `
                  0 0 15px 2px rgba(220,249,149,0.08),
                  0 0 40px 8px rgba(220,249,149,0.06),
                  0 0 80px 20px rgba(220,249,149,0.04),
                  inset 0 0 30px 4px rgba(220,249,149,0.03)
                `,
              },
            }}
          >
            <Typography
              sx={{ position: 'absolute', top: 8, right: 14, fontSize: '11px', color: 'rgba(255,255,255,0.2)', pointerEvents: 'none', zIndex: 1, letterSpacing: '0.3px' }}
            >
              {inputHint}
            </Typography>
            <SmartInput {...smartInputProps} onSubmit={validateAndAdvance} />
          </Box>

          <Box
            onClick={loading ? undefined : validateAndAdvance}
            sx={{
              width: 56, height: 56, borderRadius: '50%',
              bgcolor: loading ? 'rgba(220,249,149,0.4)' : NEON,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: loading ? 'default' : 'pointer', flexShrink: 0, transition: 'all 0.2s',
              '&:hover': loading ? {} : { transform: 'scale(1.08)', boxShadow: '0 0 20px rgba(220,249,149,0.3)' },
              '&:active': loading ? {} : { transform: 'scale(0.95)' },
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: '#1B0826' }} /> : <ArrowForwardIcon sx={{ color: '#1B0826', fontSize: 26 }} />}
          </Box>
        </Box>

        {/* Secondary actions */}
        <Box sx={{ textAlign: 'center', mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {stepType === 'password' && (
            <Typography sx={{ fontSize: '14px', color: MUTED }}>
              <span onClick={loading ? undefined : handleForgotPassword} style={{ color: NEON, cursor: 'pointer', fontWeight: 600 }}>
                Forgot password?
              </span>
            </Typography>
          )}
          {showResend && (
            <Typography sx={{ fontSize: '14px', color: MUTED }}>
              Didn't get it?{' '}
              <span onClick={loading ? undefined : handleResendCode} style={{ color: NEON, cursor: 'pointer', fontWeight: 600 }}>
                Resend code
              </span>
            </Typography>
          )}
          <Typography sx={{ fontSize: '14px', color: MUTED }}>
            Don't have an account?{' '}
            <span onClick={() => navigate('/signup')} style={{ color: NEON, cursor: 'pointer', fontWeight: 600 }}>
              Sign up
            </span>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
